<template>
  <div
    class="floating-action-menu"
    :class="{'floating-action-menu--left': isLeft}"
  >
    <!-- Menu de opções -->
    <transition name="menu-slide">
      <div v-if="isMenuOpen" class="floating-action-menu__options">
        <button 
          v-if="isTutorIAEnabled"
          class="floating-action-menu__option"
          @click="openTutorIAHandler"
        >
          <TutorIASVG class="floating-action-menu__option-icon" />
          <span class="floating-action-menu__option-text">Falar com o CyberTutor</span>
          <span class="new-badge">novo</span>
        </button>
        
        <button 
          class="floating-action-menu__option"
          @click="openFeedbackForm"
        >
          <IdeaSVG class="floating-action-menu__option-icon" />
          <span class="floating-action-menu__option-text">Enviar um feedback</span>
        </button>

        <button 
          class="floating-action-menu__option"
          @click="openHelpPage"
        >
          <HelpSVG class="floating-action-menu__option-icon" />
          <span class="floating-action-menu__option-text">Central de Ajuda</span>
        </button>
      </div>
    </transition>

    <!-- Botão principal -->
    <button 
      class="floating-action-menu__button" 
      @click="toggleMenu"
      :class="{'floating-action-menu__button--active': isMenuOpen}"
    >
      <transition name="icon-rotate" mode="out-in">
        <QuestionSVG v-if="!isMenuOpen" key="idea" />
        <CloseSVG v-else key="close" />
      </transition>
    </button>

    <!-- Modal de Feedback -->
    <FeedbackForm ref="feedbackForm" />

    <!-- TutorIA - Renderização direta sem portal -->
    <transition name="tutoria-fade">
      <div v-if="showTutorIA" class="tutoria-wrapper" :class="{'tutoria-wrapper--modal': tutorIAMode === 'modal'}">
        <!-- Overlay apenas em modo modal -->
        <div 
          v-if="tutorIAMode === 'modal'" 
          class="tutoria-modal-overlay" 
          @click.self="closeTutorIA"
        ></div>
        
        <!-- Conteúdo do TutorIA -->
        <div class="tutoria-content" :class="tutorIAMode === 'modal' ? 'tutoria-content--modal' : 'tutoria-content--floating'">
          <TutorIAChat 
            :ws-url="tutorIAWebSocketUrl"
            :show-toggle-button="true"
            @close="closeTutorIA"
            @toggle-mode="handleToggleMode"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import IdeaSVG from '@/components/svg/IdeaSVG'
  import QuestionSVG from '@/components/svg/QuestionSVG'
  import HelpSVG from '@/components/svg/HelpSVG'
  import CloseSVG from '@/components/svg/CloseSVG'
  import FeedbackForm from '@/components/feedback/FeedbackForm'
  import TutorIAChat from '@/components/tutoria/TutorIAChat.vue'
  import TutorIASVG from '@/components/svg/TutorIASVG.vue'
  import { useTutorIA } from '@/composables/useTutorIA'

  export default {
    components: {
      IdeaSVG,
      QuestionSVG,
      HelpSVG,
      CloseSVG,
      FeedbackForm,
      TutorIAChat,
      TutorIASVG,
    },

    setup() {
      const { showTutorIA, tutorIAMode, openTutorIA, closeTutorIA, toggleMode } = useTutorIA()
      
      return {
        showTutorIA,
        tutorIAMode,
        openTutorIA,
        closeTutorIA,
        toggleMode
      }
    },

    data: () => ({
      isLeft: false,
      isMenuOpen: false,
      isTutorIAEnabled: process.env.VUE_APP_TUTORIA_ENABLED === 'true',
      tutorIAWebSocketUrl: process.env.VUE_APP_WEBSOCKET_URL || 'ws://localhost:8081'
    }),
    
    mounted() {
      // Fechar menu ao clicar fora
      document.addEventListener('click', this.handleClickOutside)
    },
    
    watch: {
      $route: {
        handler ({ path }) {
          this.isLeft = path.includes('/ajuda')
          // Fecha o menu ao navegar
          this.isMenuOpen = false
        },
        immediate: true
      }
    },

    beforeDestroy() {
      document.removeEventListener('click', this.handleClickOutside)
    },

    methods: {
      handleClickOutside(event) {
        const menu = this.$el
        if (menu && !menu.contains(event.target) && this.isMenuOpen) {
          this.closeMenu()
        }
      },
      toggleMenu () {
        this.isMenuOpen = !this.isMenuOpen
      },

      closeMenu () {
        this.isMenuOpen = false
      },

      openFeedbackForm () {
        this.$refs.feedbackForm.open()
        this.closeMenu()
      },

      openTutorIAHandler () {
        this.openTutorIA()
        this.closeMenu()
      },
      
      handleToggleMode () {
        console.log('🔄 handleToggleMode chamado')
        // Não deixa o showTutorIA fechar durante a troca
        this.toggleMode()
        // Força o showTutorIA a permanecer true
        this.$nextTick(() => {
          if (!this.showTutorIA) {
            console.log('⚠️ showTutorIA foi para false, forçando true')
            this.openTutorIA()
          }
        })
      },

      openHelpPage () {
        window.location.href = '/ajuda'
        this.closeMenu()
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "~@/assets/sass/variables";

  .floating-action-menu {
    $p: &;

    // Botão principal
    &__button {
      z-index: 50;
      position: fixed;
      right: 25px;
      bottom: 25px;
      width: 3.5rem;
      height: 3.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: $fucsia300;
      border-radius: 100%;
      box-shadow: 0px 10px 20px 1px rgba(0, 0, 0, 0.25);
      transition: all 0.3s ease;
      border: none;
      cursor: pointer;

      svg { 
        fill: #fff; 
        width: 2.1875rem;
        transition: all 0.3s ease;
      }

      &:hover {
        background: #fff;
        transform: scale(1.05);
        padding: 0;

        svg { fill: $fucsia300 }
      }

      &--active {
        background: #fff;
        transform: rotate(90deg);

        svg { 
          fill: $fucsia300;
          width: 1.5rem;
        }
      }
    }

    // Menu de opções
    &__options {
      z-index: 49;
      position: fixed;
      right: 25px;
      bottom: 90px;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.875rem 1.25rem;
      background: $fucsia300;
      border: none;
      border-radius: 24px;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
      cursor: pointer;
      transition: all 0.3s ease;
      white-space: nowrap;

      &:hover {
        transform: translateX(-5px);
        box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.2);
        background: darken($fucsia300, 10%);

        #{$p}__option-text {
          color: #fff;
        }
      }
    }

    &__option-icon {
      width: 1.5rem;
      height: 1.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      fill: #fff;
      overflow: visible;

      svg {
        width: 100%;
        height: 100%;
        transform-origin: center center;
        transform: scale(2);
        display: block;
      }

    }

    &__option-text {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #fff;
      transition: color 0.3s ease;
    }

    // Responsive
    @media (max-width: 550px) {
      &__button {
        right: 10px;
        bottom: 20px;
        width: 50px;
        height: 50px;

        svg { width: 60% }
      }

      &__options {
        right: 10px;
        bottom: 80px;
      }

      &__option {
        padding: 0.75rem 1rem;
        
        &-text {
          font-size: 0.875rem;
        }

        &-icon {
          font-size: 1.25rem;
        }
      }
    }

    // Posição à esquerda (para páginas de ajuda)
    &--left {
      #{$p}__button {
        right: auto;
        left: 25px;

        @media (max-width: 550px) { left: 10px }
      }

      #{$p}__options {
        right: auto;
        left: 25px;

        @media (max-width: 550px) { left: 10px }
      }

      &__option:hover {
        transform: translateX(5px);
      }
    }
  }

  // Animações
  .menu-slide-enter-active,
  .menu-slide-leave-active {
    transition: all 0.3s ease;
  }

  .menu-slide-enter-from {
    opacity: 0;
    transform: translateY(10px);
  }

  .menu-slide-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }

  .icon-rotate-enter-active,
  .icon-rotate-leave-active {
    transition: all 0.3s ease;
  }

  .icon-rotate-enter-from {
    opacity: 0;
    transform: rotate(-90deg) scale(0.5);
  }

  .icon-rotate-leave-to {
    opacity: 0;
    transform: rotate(90deg) scale(0.5);
  }

  // Estilos do TutorIA
  .tutoria-wrapper {
    position: fixed;
    z-index: 9999;

    &--modal {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .tutoria-modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }

  .tutoria-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;

    &--modal {
      width: 90%;
      max-width: 800px;
      height: 90vh;
      margin: auto;
    }

    &--floating {
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 400px;
      max-width: calc(100vw - 40px);
      height: 600px;

      @media (max-width: 768px) {
        bottom: 10px;
        right: 10px;
        width: calc(100vw - 20px);
        height: calc(100vh - 20px);
      }
    }
  }

  .tutoria-fade-enter-active,
  .tutoria-fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .tutoria-fade-enter-from,
  .tutoria-fade-leave-to {
    opacity: 0;
  }
</style>
